import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { AssigneeSelector } from "./assignee-selector";
import { PriorityBadge, StatusBadge } from "./badges/conversation-badges";

export interface ConversationDetailsPanelProps {
  conversation: ConversationListItemResponse;
}

/**
 * Right panel component that displays conversation details.
 * Shows overview (status, priority) and customer info placeholders.
 */
export const ConversationDetailsPanel = memo(function ConversationDetailsPanel({
  conversation,
}: ConversationDetailsPanelProps) {
  return (
    <Flex dir="column" flex={1} gap="lg" overflowY="auto" p="xl">
      {/* Overview Section */}
      <ConversationOverviewSection conversation={conversation} />

      {/* Customer Info Section - Placeholder */}
      <CustomerInfoSection />

      {/* Orders Section - Placeholder */}
      <OrdersSection />
    </Flex>
  );
});

interface ConversationOverviewSectionProps {
  conversation: ConversationListItemResponse;
}

const ConversationOverviewSection = memo(function ConversationOverviewSection({
  conversation,
}: ConversationOverviewSectionProps) {
  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      dir="column"
      gap="md"
      pb="lg"
    >
      <Text fontSize="sm" fontWeight="semibold" textColor="primary">
        Overview
      </Text>

      {/* Status Row */}
      <Flex align="center" gap="md" justify="space-between">
        <Text fontSize="xs" textColor="secondary">
          Status
        </Text>
        <StatusBadge status={conversation.status} />
      </Flex>

      {/* Priority Row */}
      {conversation.priority && (
        <Flex align="center" gap="md" justify="space-between">
          <Text fontSize="xs" textColor="secondary">
            Priority
          </Text>
          <PriorityBadge priority={conversation.priority} />
        </Flex>
      )}

      {/* Assignee Row */}
      <AssigneeSelector
        conversationId={conversation.id}
        mongoAssigneeId={conversation.mongoAssigneeId}
      />
    </Flex>
  );
});

const CustomerInfoSection = memo(function CustomerInfoSection() {
  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      dir="column"
      gap="md"
      pb="lg"
    >
      <Text fontSize="sm" fontWeight="semibold" textColor="primary">
        Customer
      </Text>
      <Text fontSize="xs" textColor="tertiary">
        Customer info will be displayed here
      </Text>
    </Flex>
  );
});

const OrdersSection = memo(function OrdersSection() {
  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" fontWeight="semibold" textColor="primary">
        Orders
      </Text>
      <Text fontSize="xs" textColor="tertiary">
        Orders will be displayed here
      </Text>
    </Flex>
  );
});
